import { useQueryClient } from "@tanstack/react-query";
import React, { useState, useEffect, useMemo } from "react";
import { Button, Badge, Spinner, Modal, Form, Card } from "react-bootstrap";
import { FaEye, FaPlus, FaLock } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import NewIntervention from "../../components/Modals/Create/NewIntervention";
import { useAuth } from "../../context/AuthContext";
import { useCloseTicket } from "../../hooks/TICKET/useCloseTicket";
import { useFacturationModes } from "../../hooks/TICKET/useFacturationModes";
import { useContratsByClientId } from "../../hooks/useGetContratClient";
import { useClientChamps } from "../../hooks/useClientChamps";
import { getApplicableChamps, TICKET_TO_FIELD_STATUS } from "../../utils/customFields";
import { getStatusVariant, statusNeedsDarkText } from "../../utils/ticketStatus";
import CustomFieldInput from "../../components/CustomFields/CustomFieldInput";

// ---------------------------------------------------------------------------
// "Contrat de maintenance" has is_active:false in DB so the API never returns
// it. We inject it manually at the top of the list when the client has an
// active contract.
// ---------------------------------------------------------------------------
const CONTRAT_MAINTENANCE = { id: 1, label: "Contrat de maintenance" };

const TicketRow = ({ ticket, listState, asCard = false }) => {
  // ── Router ────────────────────────────────────────────────────────────────
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // ── Auth ──────────────────────────────────────────────────────────────────
  const { hasPermission } = useAuth();

  // ── Local UI state ────────────────────────────────────────────────────────
  const [showNewIntervention,     setShowNewIntervention]     = useState(false);
  const [showConfirmClose,        setShowConfirmClose]        = useState(false);
  const [selectedFacturationMode, setSelectedFacturationMode] = useState(null);
  const [customFieldValues,       setCustomFieldValues]       = useState({});

  // ── Remote data & mutations ───────────────────────────────────────────────
  const closeMutation = useCloseTicket();
  const { data: facturationModesData, isLoading: facturationLoading } = useFacturationModes({ enabled: showConfirmClose });
  const { data: contratData,          isLoading: contractLoading    } = useContratsByClientId(ticket.clientId, { enabled: showConfirmClose });

  // Champs personnalisés — chargés à l'ouverture du modal de clôture
  const { data: clientChampsData, isLoading: champsLoading } = useClientChamps(
    ticket.clientId,
    { enabled: showConfirmClose }
  );
  const clientChamps = clientChampsData?.data ?? [];
  // Champs applicables : depuis le statut actuel du ticket jusqu'à 'closed'
  // (inclut les étapes sautées, ex. open → closed affiche aussi les champs 'in_progress')
  const currentFieldStatus = TICKET_TO_FIELD_STATUS[ticket.rawStatus] ?? 'open';
  const champsACloturer    = getApplicableChamps(clientChamps, currentFieldStatus, 'closed');

  // ── Derived values ────────────────────────────────────────────────────────
  const facturationModes = useMemo(() => facturationModesData?.data || [], [facturationModesData]);
  const contracts        = Array.isArray(contratData?.data) ? contratData.data : [];
  const hasContract      = contracts.some(c => !c.expire_date || new Date(c.expire_date) > new Date());
  const sortedInterventions = useMemo(() => {
    const interventions = ticket.interventions || [];
    return [...interventions].sort((a, b) => {
      const aTime = new Date(a?.start_date || a?.created_at || 0).getTime();
      const bTime = new Date(b?.start_date || b?.created_at || 0).getTime();
      return bTime - aTime;
    });
  }, [ticket]);
  const isEnCours        = ticket.statut === "En cours" || ticket.rawStatus === "in_progress";
  const isModalLoading   = facturationLoading || contractLoading || champsLoading;

  // Build the billing-method list for the close modal.
  // Contrat de maintenance is prepended only when the client has a contract.
  const availableFacturationModes = useMemo(() => [
    ...(hasContract ? [CONTRAT_MAINTENANCE] : []),
    ...facturationModes.filter(m => m.id !== 1 && (m.is_active || m.is_default)),
  ], [hasContract, facturationModes]);

  // ── Effects ───────────────────────────────────────────────────────────────

  // Auto-select the best billing method when the close modal opens.
  useEffect(() => {
    const ready = showConfirmClose && !isModalLoading && availableFacturationModes.length > 0;
    if (!ready || selectedFacturationMode) return;

    const defaultId = hasContract
      ? CONTRAT_MAINTENANCE.id
      : availableFacturationModes[0]?.id;

    setSelectedFacturationMode(defaultId);
  }, [showConfirmClose, isModalLoading, availableFacturationModes, selectedFacturationMode, hasContract]);

  // ── Helpers ───────────────────────────────────────────────────────────────

  const formatDate = (value) => {
    if (!value) return "N/A";
    try {
      const d = value instanceof Date ? value : new Date(value);
      if (isNaN(d.getTime())) return "N/A";
      return d.toLocaleString("fr-FR", {
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit",
      });
    } catch {
      return "N/A";
    }
  };

  const statusVariant = getStatusVariant(ticket.rawStatus);

  const closeModal = () => {
    setShowConfirmClose(false);
    setSelectedFacturationMode(null);
    setCustomFieldValues({});
  };

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleCloseTicket = () => {
    if (!selectedFacturationMode) {
      toast.error("Veuillez sélectionner une méthode de facturation");
      return;
    }
    closeMutation.mutate(
      { id: ticket.id, facturationModeId: selectedFacturationMode },
      {
        onSuccess: () => { toast.success("Ticket clôturé !"); closeModal(); },
        onError:   (err) => { toast.error("Erreur: " + err.message); closeModal(); },
      }
    );
  };

  // ── Sub-components ────────────────────────────────────────────────────────

  // Shared modals rendered once regardless of card/table view
  const modals = (
    <>
      {/* Close-ticket confirmation modal */}
      <Modal show={showConfirmClose} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Clôturer le ticket</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Êtes-vous sûr de vouloir clôturer ce ticket ?</p>

          <Form.Group className="mt-3">
            <Form.Label>
              Méthode de facturation <span className="text-danger">*</span>
            </Form.Label>

            {isModalLoading ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
              </div>
            ) : (
              <Form.Select
                value={selectedFacturationMode || ""}
                onChange={(e) => setSelectedFacturationMode(Number(e.target.value))}
                required
              >
                <option value="">Sélectionner une méthode</option>
                {availableFacturationModes.map((mode) => (
                  <option key={mode.id} value={mode.id}>{mode.label}</option>
                ))}
              </Form.Select>
            )}
          </Form.Group>

          {/* Champs personnalisés applicables à cette clôture */}
          {!isModalLoading && champsACloturer.length > 0 && (
            <>
              <hr />
              <p className="fw-semibold mb-2">Champs complémentaires</p>
              {champsACloturer.map(champ => (
                <CustomFieldInput
                  key={champ.id}
                  champ={champ}
                  value={customFieldValues[champ.id] ?? ""}
                  onChange={val => setCustomFieldValues(prev => ({ ...prev, [champ.id]: val }))}
                />
              ))}
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeModal}>Annuler</Button>
          <Button
            variant="warning"
            onClick={handleCloseTicket}
            disabled={closeMutation.isLoading || !selectedFacturationMode || isModalLoading}
          >
            {closeMutation.isLoading ? "Clôture..." : "Clôturer"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* New intervention modal */}
      <NewIntervention
      showNewIntervention={showNewIntervention}
      setShowNewIntervention={setShowNewIntervention}
      ticketId={ticket.id}
      onSuccess={() => {
        queryClient.invalidateQueries({ queryKey: ["tickets"] });
      }}
    />
    </>
  );

  // Intervention list — shared between card and table views
  const interventionList = sortedInterventions.length > 0
    ? sortedInterventions.map((intervention, idx) => {
        const daysDiff = Math.floor(
          (new Date() - new Date(intervention.start_date || intervention.created_at))
          / (1000 * 60 * 60 * 24)
        );
        // Color coding: blue > 15 days old, yellow > 7 days old
        const ageClass = daysDiff > 15 ? "text-danger" : daysDiff > 7 ? "text-warning" : "";
        return (
          <div
            key={intervention.id || idx}
            className={ageClass}
            style={{
              padding: "2px 0",
              borderBottom: idx < sortedInterventions.length - 1 ? "1px solid #ddd" : "none",
              fontSize: "0.875rem",
            }}
          >
            {formatDate(intervention.start_date || intervention.created_at)}
          </div>
        );
      })
    : null;

  // Action buttons — shared between card and table views
  const actionButtons = (
    <>
      <Button
        variant="secondary"
        size="sm"
        title="Voir le ticket"
        style={{ width: "32px", height: "32px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
        onClick={() => navigate(`/ticket/${ticket.id}`, {
          state: { from: location.pathname, listState: { ...listState, scrollY: window.scrollY } }
        })}
      >
        <FaEye />
      </Button>

      {hasPermission("CREATE_INTERVENTION") && (
        <Button
          variant="primary"
          size="sm"
          title="Ajouter une intervention"
          style={{ width: "32px", height: "32px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowNewIntervention(true)}
        >
          <FaPlus style={{ color: "white" }} />
        </Button>
      )}

      {isEnCours && hasPermission("CLOSE_TICKET") && (
        <Button
          variant="warning"
          size="sm"
          title="Clôturer le ticket"
          style={{ width: "32px", height: "32px", padding: 0, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={() => setShowConfirmClose(true)}
          disabled={closeMutation.isLoading}
        >
          <FaLock style={{ color: "white" }} />
        </Button>
      )}
    </>
  );

  // ── Render: Mobile card ───────────────────────────────────────────────────
  if (asCard) {
    return (
      <>
        <Card className="h-100 shadow-sm">
          <Card.Body className="d-flex flex-column gap-2">

            {/* Header row: ID + status badge */}
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted small">#{ticket.id}</span>
              <Badge bg={statusVariant} text={statusNeedsDarkText(statusVariant) ? "dark" : undefined}>
                {ticket.statut || ticket.status || "Non assigné"}
              </Badge>
            </div>

            <div className="fw-bold" style={{ fontSize: "0.95rem" }}>
              {ticket.titre || ticket.title}
            </div>

            <div className="small text-muted">
              <span className="fw-semibold">Début :</span>{" "}
              {formatDate(ticket.dateDebut || ticket.created_at)}
            </div>

            <div className="small">
              <span className="fw-semibold text-muted">Client :</span> {ticket.client}
            </div>

            <div className="small">
              <span className="fw-semibold text-muted">Intervenant(s) :</span>{" "}
              {Array.isArray(ticket.intervenants) && ticket.intervenants.length > 0
                ? ticket.intervenants.map((i, idx) => (
                    <Badge key={idx} bg="body" text="body" className="me-1">{i}</Badge>
                  ))
                : <span className="text-muted fst-italic">Non assigné</span>}
            </div>

            <div className="small">
              <span className="fw-semibold text-muted">Intervention(s) :</span>
              {interventionList
                ? <div className="mt-1" style={{ maxHeight: "70px", overflowY: "auto" }}>{interventionList}</div>
                : <span className="text-muted fst-italic ms-1">Aucune</span>}
            </div>

            {/* Action buttons */}
            <div className="d-flex gap-2 mt-auto pt-2 border-top flex-wrap">
              {actionButtons}
            </div>

          </Card.Body>
        </Card>
        {modals}
      </>
    );
  }

  // ── Render: Desktop table row ─────────────────────────────────────────────
  return (
    <>
      <tr>
        <td className="align-middle">{ticket.id}</td>
        <td className="align-middle">{ticket.titre || ticket.title}</td>
        <td className="align-middle">{formatDate(ticket.dateDebut || ticket.created_at)}</td>
        <td className="align-middle">{ticket.client}</td>

        <td className="align-middle">
          {Array.isArray(ticket.intervenants) && ticket.intervenants.length > 0
            ? ticket.intervenants.join(", ")
            : "Non assigné"}
        </td>

        <td className="align-middle">
          <div style={{ maxHeight: "80px", overflowY: "auto" }}>
            {interventionList ?? <span className="text-muted">Aucune intervention</span>}
          </div>
        </td>

        <td className="align-middle text-center">
          <Badge bg={statusVariant} text={statusNeedsDarkText(statusVariant) ? "dark" : undefined}>
            {ticket.statut}
          </Badge>
        </td>

        <td className="align-middle">
          <div className="d-flex gap-2 justify-content-center">
            {actionButtons}
          </div>
        </td>
      </tr>
      {modals}
    </>
  );
};

export default TicketRow;